import { useEffect, useState } from "react";
import { GraphQLQuery } from "@aws-amplify/api";
import dayjs from "dayjs";
import { useSelector } from "app/hooks";
import { getPortfolioData } from "../../../graphql/queries";
import { GetPortfolioDataQuery } from "API";
import {
  getCustomPeriodLength,
  getCustomStartDateBounds,
  getReportingPeriod,
  isCustomStartDateValid,
} from "helpers/reportingPeriod";
import { formatCurrency } from "helpers/currency";
import {
  contributionProducts,
  otherProducts,
  withdrawalProducts,
} from "helpers/products";
import { client } from "graphql/client";
import { gql } from "@apollo/client";

interface DataPoint {
  date: string;
  value: number;
  name?: string;
}

interface ChartData {
  seriesData: Array<DataPoint>;
}

export const usePerformanceChart = () => {
  const {
    selectedFunds,
    reportingPeriod: reportingPeriodYears,
    isCustomPeriod,
    customStartDate,
  } = useSelector((state) => state.investmentFunds);
  const {
    investmentAmount,
    clientFunds,
    netAllocation,
    regularContributionNet,
  } = useSelector((state) => state.investmentAmount);

  const {
    drawdownPercent,
    drawdownMonetaryAmount,
    drawdownType,
    hasDrawdown,
    hasRegular,
    hasSingle,
    product,
  } = useSelector((state) => state.investmentDetails);
  const [chartData, setChartData] = useState<ChartData | undefined>(undefined);
  const [debouncedCustomStartDate, setDebouncedCustomStartDate] =
    useState(customStartDate);
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedCustomStartDate(customStartDate),
      400,
    );
    return () => clearTimeout(timer);
  }, [customStartDate]);
  const { startDate, endDate } = getReportingPeriod(
    reportingPeriodYears as number,
    isCustomPeriod ? debouncedCustomStartDate : undefined,
  );

  const { minStartDate, maxStartDate } =
    getCustomStartDateBounds(selectedFunds);
  const customStartDateValid = isCustomStartDateValid(
    debouncedCustomStartDate,
    minStartDate,
    maxStartDate,
  );
  const invalidCustomPeriod = isCustomPeriod && !customStartDateValid;
  const { years: customPeriodYears, months: customPeriodMonths } =
    getCustomPeriodLength(startDate, endDate);
  const indefiniteArticle =
    [8, 11, 18, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89].indexOf(
      isCustomPeriod ? customPeriodYears : (reportingPeriodYears as number),
    ) !== -1
      ? "an"
      : "a";

  const allocationDateText = isCustomPeriod
    ? `on ${dayjs(startDate).format("DD MMM YYYY")}`
    : `in ${dayjs(startDate).format("MMMM YYYY")}`;
  const investmentAmountStr =
    contributionProducts.includes(product) && !hasSingle
      ? "No single premium invested"
      : `€${formatCurrency(
          clientFunds,
        )} invested with ${netAllocation}% allocation ${allocationDateText}`;
  const chartTitle = `${investmentAmountStr}`;
  const customPeriodText = customPeriodMonths
    ? `${customPeriodYears} year ${customPeriodMonths} month`
    : `${customPeriodYears} year`;
  let chartSubTitle = isCustomPeriod
    ? `over ${indefiniteArticle} ${customPeriodText} period`
    : `over ${indefiniteArticle} ${reportingPeriodYears} year period`;

  if (withdrawalProducts.includes(product)) {
    if (drawdownType === "percent" && drawdownPercent > 0) {
      chartSubTitle = `${chartSubTitle} with ${drawdownPercent}% drawdown`;
    }
    if (drawdownType === "monetary" && drawdownMonetaryAmount > 0) {
      chartSubTitle = `${chartSubTitle} with a €${formatCurrency(
        drawdownMonetaryAmount,
      )} drawdown`;
    }
  }

  if (contributionProducts.includes(product)) {
    // less than 0 as negative drawdown equals contribution
    if (drawdownMonetaryAmount < 0 && hasRegular) {
      chartSubTitle = `${chartSubTitle} with a €${formatCurrency(
        Math.abs(drawdownMonetaryAmount),
      )} regular premium with ${netAllocation}% allocation`;
    }
  }

  useEffect(() => {
    const loadFunds = async (): Promise<void> => {
      setChartData(undefined);
      const funds = selectedFunds
        .filter((fund) => !!fund.allocation)
        .map(({ citicode, allocation }) => ({
          citicode,
          allocation,
        }));
      const input = {
        funds,
        startDate,
        endDate,
        investmentAmount,
        drawdownPercent,
        drawdownMonetaryAmount,
        netAllocation,
      };

      let cleanedInput = { ...input };

      if (hasDrawdown && withdrawalProducts.includes(product)) {
        if (drawdownType === "percent") {
          cleanedInput = { ...input, drawdownMonetaryAmount: 0 };
        } else if (drawdownType === "monetary") {
          cleanedInput = { ...input, drawdownPercent: 0 };
        }
      }

      if (contributionProducts.includes(product)) {
        if (!hasSingle) {
          cleanedInput = { ...input };
          if (hasRegular) {
            cleanedInput.drawdownMonetaryAmount = regularContributionNet * -1;
          }
        }
        if (!hasRegular) {
          cleanedInput = { ...input, drawdownMonetaryAmount: 0 };
        }
      }

      if (otherProducts.includes(product)) {
        cleanedInput = {
          ...input,
          drawdownMonetaryAmount: 0,
          drawdownPercent: 0,
        };
      }

      const { data } = await client.query<GraphQLQuery<GetPortfolioDataQuery>>({
        query: gql(getPortfolioData),
        variables: { input: cleanedInput },
      });
      if (data?.getPortfolioData?.seriesData) {
        setChartData({ seriesData: data.getPortfolioData.seriesData });
      }
    };
    const periodSelected = isCustomPeriod
      ? customStartDateValid
      : !!reportingPeriodYears;
    if (periodSelected && selectedFunds && investmentAmount) {
      loadFunds();
    } else if (invalidCustomPeriod) {
      setChartData(undefined);
    }
  }, [
    reportingPeriodYears,
    debouncedCustomStartDate,
    isCustomPeriod,
    customStartDateValid,
    invalidCustomPeriod,
    selectedFunds,
    investmentAmount,
    startDate,
    endDate,
    drawdownPercent,
    drawdownType,
    drawdownMonetaryAmount,
    regularContributionNet,
    netAllocation,
    hasDrawdown,
    hasSingle,
    hasRegular,
    product,
  ]);

  return {
    loading: !chartData && !invalidCustomPeriod,
    invalidCustomPeriod,
    chartTitle,
    chartSubTitle,
    chartData,
    reportingPeriodYears,
  };
};
